import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  nombre: "",
  telefono: "",
  marca: "",
  modelo: "",
  anio: "",
  matricula: "",
  pieza: "",
  descripcion: "",
  urgencia: "",
};

const fieldCls =
  "bg-industrial-850 border-industrial-700 focus-visible:ring-brand-amber text-slate-100 placeholder:text-slate-600 h-12 rounded-sm";

const Field = ({ label, htmlFor, required, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="font-mono text-[11px] uppercase tracking-widest text-slate-400">
      {label} {required && <span className="text-brand-orange">*</span>}
    </Label>
    {children}
  </div>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [foto, setFoto] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen supera 3MB. Elige una más ligera.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.urgencia) {
      toast.error("Selecciona el nivel de urgencia.");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        anio: parseInt(form.anio, 10),
        foto,
      };
      const { data } = await axios.post(`${API}/quotes`, payload);
      toast.success(data.message || "Solicitud enviada correctamente.");
      setForm(initialForm);
      setFoto(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo enviar la solicitud. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="presupuesto" className="relative py-24 sm:py-32" data-testid="quote-form-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-amber mb-4">Formulario directo</p>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Solicita tu <span className="text-brand-orange">presupuesto</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto text-sm sm:text-base">
            Sin registro previo. Te respondemos por WhatsApp en menos de 2 horas laborables.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="bg-industrial-900 border border-industrial-700 rounded-md p-6 sm:p-10 grid sm:grid-cols-2 gap-6"
          data-testid="quote-form"
        >
          <Field label="Nombre y apellidos" htmlFor="nombre" required>
            <Input id="nombre" data-testid="quote-form-name-input" required value={form.nombre} onChange={set("nombre")} placeholder="Ej: Carlos Martínez" className={fieldCls} />
          </Field>
          <Field label="WhatsApp (recibirás el presupuesto aquí)" htmlFor="telefono" required>
            <Input id="telefono" data-testid="quote-form-phone-input" required type="tel" minLength={9} value={form.telefono} onChange={set("telefono")} placeholder="Ej: 644 856 072" className={fieldCls} />
          </Field>
          <Field label="Marca del coche" htmlFor="marca" required>
            <Input id="marca" data-testid="quote-form-brand-input" required value={form.marca} onChange={set("marca")} placeholder="Ej: Audi, BMW, Seat..." className={fieldCls} />
          </Field>
          <Field label="Modelo y versión" htmlFor="modelo" required>
            <Input id="modelo" data-testid="quote-form-model-input" required value={form.modelo} onChange={set("modelo")} placeholder="Ej: Golf VII 2.0 TDI" className={fieldCls} />
          </Field>
          <Field label="Año de fabricación" htmlFor="anio" required>
            <Input id="anio" data-testid="quote-form-year-input" required type="number" min="1950" max="2030" value={form.anio} onChange={set("anio")} placeholder="Ej: 2018" className={fieldCls} />
          </Field>
          <Field label="Matrícula" htmlFor="matricula">
            <Input id="matricula" data-testid="quote-form-plate-input" value={form.matricula} onChange={set("matricula")} maxLength={12} placeholder="Ej: 1234 BCD (recomendado)" className={`${fieldCls} font-mono uppercase`} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Pieza solicitada" htmlFor="pieza" required>
              <Input id="pieza" data-testid="quote-form-part-input" required value={form.pieza} onChange={set("pieza")} placeholder="Ej: Faro delantero izquierdo, caja de cambios..." className={fieldCls} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Detalles adicionales o referencia" htmlFor="descripcion">
              <Textarea id="descripcion" data-testid="quote-form-description-input" value={form.descripcion} onChange={set("descripcion")} placeholder="Estado deseado (nuevo/usado), lado, código de motor..." className={`${fieldCls} h-28 resize-none`} />
            </Field>
          </div>
          <Field label="Nivel de urgencia" htmlFor="urgencia" required>
            <Select value={form.urgencia} onValueChange={(v) => setForm((f) => ({ ...f, urgencia: v }))}>
              <SelectTrigger id="urgencia" data-testid="quote-form-urgency-select" className={fieldCls}>
                <SelectValue placeholder="Selecciona urgencia" />
              </SelectTrigger>
              <SelectContent className="bg-industrial-850 border-industrial-700 text-slate-100">
                <SelectItem value="baja" data-testid="quote-form-urgency-baja">Baja · Busco el mejor precio</SelectItem>
                <SelectItem value="media" data-testid="quote-form-urgency-media">Media · La necesito en 3-5 días</SelectItem>
                <SelectItem value="alta" data-testid="quote-form-urgency-alta">Alta · Vehículo inmovilizado</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400 block mb-2">
              Foto de la pieza o ficha técnica
            </Label>
            {foto ? (
              <div className="relative w-40" data-testid="quote-form-photo-preview">
                <img src={foto} alt="Vista previa" className="w-40 h-40 object-cover rounded-sm border border-industrial-600" />
                <button
                  type="button"
                  onClick={() => setFoto(null)}
                  data-testid="quote-form-photo-remove"
                  className="absolute -top-2 -right-2 bg-brand-orange text-white rounded-full p-1 hover:bg-brand-amber transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="foto"
                data-testid="quote-form-photo-upload"
                className="flex items-center justify-center gap-3 border border-dashed border-industrial-600 hover:border-brand-amber rounded-sm h-24 cursor-pointer text-slate-500 hover:text-brand-amber transition-colors"
              >
                <ImagePlus className="w-5 h-5" />
                <span className="font-mono text-xs uppercase tracking-widest">Adjuntar imagen (máx. 3MB)</span>
                <input id="foto" type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
            )}
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="quote-form-submit-button"
            className="sm:col-span-2 bg-brand-orange hover:bg-brand-amber disabled:opacity-60 text-white font-display font-black uppercase tracking-widest text-lg py-5 rounded-sm transition-all duration-300 hover:translate-y-[-2px] flex items-center justify-center gap-3"
          >
            {sending && <Loader2 className="w-5 h-5 animate-spin" />}
            {sending ? "Enviando..." : "Enviar solicitud de presupuesto"}
          </button>
          <p className="sm:col-span-2 text-center font-mono text-[10px] uppercase tracking-widest text-slate-600">
            Tus datos solo se usan para gestionar tu presupuesto
          </p>
        </motion.form>
      </div>
    </section>
  );
};
